import { useState } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { customInstance, type Feedback } from '@edison/api-client-react';
import { Button } from '@/components/ui/button.js';

const apiBase = typeof window !== 'undefined' ? (import.meta.env['VITE_API_BASE_URL'] ?? '') : '';

const STATUS_LABEL: Record<string, string> = {
  open:      'Open',
  in_review: 'In Review',
  planned:   'Planned',
  resolved:  'Resolved',
  closed:    'Closed',
};

const STATUS_COLOR: Record<string, string> = {
  open:      'text-red-500',
  in_review: 'text-amber-fg',
  planned:   'text-indigo-500',
  resolved:  'text-emerald-600',
  closed:    'text-emerald-600',
};

const STATUS_OPTIONS = ['open', 'in_review', 'planned', 'resolved', 'closed'] as const;
type FeedbackStatus = typeof STATUS_OPTIONS[number];

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function StatusToggle({ ticketId, current, onUpdate }: { ticketId: string; current: string; onUpdate: (status: FeedbackStatus) => void }) {
  const [loading, setLoading] = useState(false);

  async function update(status: FeedbackStatus) {
    setLoading(true);
    try {
      await customInstance({ url: `/api/help/feedback/${ticketId}`, method: 'PATCH', data: { status } });
      onUpdate(status);
    } catch {
      // no-op — error already logged by axios interceptor
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex gap-1 flex-wrap">
      {STATUS_OPTIONS.map((s) => (
        <button
          key={s}
          type="button"
          disabled={loading || s === current}
          onClick={() => void update(s)}
          className={`px-2 py-0.5 text-xs rounded-full border font-medium transition-colors ${
            s === current
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-border text-muted-foreground hover:border-muted-foreground bg-background'
          }`}
        >
          {STATUS_LABEL[s] ?? s}
        </button>
      ))}
    </div>
  );
}

function TicketRow({ ticket, onStatusUpdate }: { ticket: Feedback & { status: string }; onStatusUpdate: (id: string, status: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  const status = ticket.status;

  return (
    <div className="border-b border-border last:border-0 py-3">
      <button
        type="button"
        className="w-full text-left"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm font-medium leading-snug flex-1">{ticket.title}</p>
          <span className={`text-xs font-medium shrink-0 mt-0.5 ${STATUS_COLOR[status] ?? 'text-muted-foreground'}`}>
            {STATUS_LABEL[status] ?? status}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-muted-foreground capitalize">
            {ticket.type === 'bug_report' ? 'Bug' : 'Feature'}
            {ticket.priority ? ` · ${ticket.priority}` : ''}
          </span>
          {ticket.createdAt && (
            <span className="text-xs text-muted-foreground/60">· {relativeTime(ticket.createdAt)}</span>
          )}
        </div>
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          {ticket.description && (
            <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
              {ticket.description}
            </p>
          )}
          {ticket.pageUrl && (
            <p className="text-xs text-muted-foreground">Page: <code className="bg-muted px-1 rounded">{ticket.pageUrl}</code></p>
          )}
          <div>
            <p className="text-xs font-medium mb-1.5">Update status</p>
            <StatusToggle
              ticketId={ticket.id ?? ''}
              current={status}
              onUpdate={(s) => onStatusUpdate(ticket.id ?? '', s)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function DevSupportInboxPage() {
  const [statusOverrides, setStatusOverrides] = useState<Record<string, string>>({});

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dev-support-inbox'],
    queryFn: async () => {
      const res = await customInstance<{ data: { items: Feedback[] } }>({
        url: `${apiBase}/api/help/feedback/all`,
        method: 'GET',
      });
      return res.data.items;
    },
  });

  const items = (data ?? []).map((t) => ({
    ...t,
    status: statusOverrides[t.id ?? ''] ?? t.status ?? 'open',
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Support Inbox</h1>
          <p className="text-sm text-muted-foreground mt-1">
            All feedback from this tenant — developer-only view
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={() => void refetch()}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
          Refresh
        </Button>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-14 rounded-lg skeleton" />
          ))}
        </div>
      )}

      {!isLoading && error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center gap-2 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Failed to load inbox. You may not have developer role access.</span>
          <Button variant="secondary" size="sm" onClick={() => void refetch()} className="ml-auto">
            Retry
          </Button>
        </div>
      )}

      {!isLoading && !error && items.length === 0 && (
        <p className="text-sm text-muted-foreground py-6">No feedback submitted yet.</p>
      )}

      {!isLoading && !error && items.length > 0 && (
        <div>
          {items.map((ticket) => (
            <TicketRow
              key={ticket.id}
              ticket={ticket}
              onStatusUpdate={(id, status) => {
                setStatusOverrides((prev) => ({ ...prev, [id]: status }));
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
